import React, { useEffect } from 'react';
import { FormControlLabel, Checkbox, MenuItem, Select, TextField, FormGroup } from '@mui/material';
import { H3 } from '../../../../../../../components/UI/generalLayout/Layout.styled';
import useResponsiveInfo from '../../../../../../../hooks/useResponsiveInfo';
import moment from 'moment';

interface INavegacaoField {
  isViewState: boolean;
  navegacao: any;
  setNavegacao: (value: any) => void;
  selectedCareLines: any[];
}

const NavegacaoField = ({ isViewState, navegacao, setNavegacao, selectedCareLines }: INavegacaoField) => {
  const { isMobile } = useResponsiveInfo();

  useEffect(() => {
    if (!isViewState && navegacao && !navegacao.isInitialized) {
      setNavegacao({
        ...navegacao,
        linhasCuidadoId: selectedCareLines.map((cl: any) => cl.id || cl.linhaCuidadoId),
        isInitialized: true
      });
    }
  }, [selectedCareLines, isViewState]);

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setNavegacao({
        risco: '',
        linhasCuidadoId: selectedCareLines.map((cl: any) => cl.id || cl.linhaCuidadoId),
        motivo: '',
        dataAprazamento: moment().format('YYYY-MM-DD'),
        turnoAprazamento: '',
        isInitialized: true
      });
    } else {
      setNavegacao(null);
    }
  };

  const getValidTurnos = (dateStr: string) => {
    const all = ['Manhã', 'Tarde', 'Noite'];
    if (!dateStr) return all;
    const selected = moment(dateStr, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    if (selected.isSame(today, 'day')) {
      const hour = moment().hour();
      if (hour >= 18) return [];
      if (hour >= 12) return ['Noite'];
      return ['Tarde', 'Noite'];
    }
    return all;
  };

  const validTurnos = getValidTurnos(navegacao?.dataAprazamento);

  if (isViewState) {
    if (!navegacao) {
      return <div style={{ color: '#666', fontStyle: 'italic', padding: '10px 0' }}>Nenhum processo de navegação iniciado neste atendimento.</div>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15, padding: '10px 0' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 20 }}>
          <div style={{ flex: 1 }}>
            <H3>Risco:</H3>
            <TextField value={navegacao.risco || ''} disabled fullWidth size="small" variant="outlined" sx={{ backgroundColor: '#f5f5f5' }} />
          </div>
          <div style={{ flex: 1 }}>
            <H3>Plano de Aprazamento:</H3>
            <TextField
              value={navegacao.dataAprazamento ? `${moment(navegacao.dataAprazamento).format('DD/MM/YYYY')} - ${navegacao.turnoAprazamento}` : ''}
              disabled
              fullWidth
              size="small"
              variant="outlined"
              sx={{ backgroundColor: '#f5f5f5' }}
            />
          </div>
        </div>
        <div>
          <H3>Linhas de Cuidado:</H3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 5 }}>
            {navegacao.linhasCuidado?.length > 0 ? (
              navegacao.linhasCuidado.map((lcName: string, idx: number) => (
                <span key={idx} style={{ backgroundColor: '#e0f7fa', color: '#006064', padding: '5px 10px', borderRadius: 4, fontSize: '0.875rem', fontWeight: 500 }}>
                  {lcName}
                </span>
              ))
            ) : (
              <span style={{ color: '#999', fontStyle: 'italic' }}>Nenhuma linha de cuidado selecionada.</span>
            )}
          </div>
        </div>
        <div>
          <H3>Motivo da Inclusão:</H3>
          <TextField value={navegacao.motivo || ''} disabled fullWidth multiline rows={3} size="small" variant="outlined" sx={{ backgroundColor: '#f5f5f5' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '10px 0' }}>
      <FormControlLabel
        control={<Checkbox checked={!!navegacao} onChange={(e) => handleToggle(e.target.checked)} color="primary" />}
        label="Incluir paciente no processo de navegação"
        style={{ marginBottom: 15 }}
      />

      {!!navegacao && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginTop: 10 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 20 }}>
            <div style={{ flex: 1 }}>
              <H3>Risco <span style={{ color: 'red' }}>*</span></H3>
              <Select
                value={navegacao.risco || ''}
                onChange={(e) => setNavegacao({ ...navegacao, risco: e.target.value })}
                fullWidth
                size="small"
                displayEmpty
              >
                <MenuItem value="" disabled>Selecionar risco</MenuItem>
                <MenuItem value="Baixo">Baixo</MenuItem>
                <MenuItem value="Médio">Médio</MenuItem>
                <MenuItem value="Alto">Alto</MenuItem>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <H3>Data do Aprazamento <span style={{ color: 'red' }}>*</span></H3>
              <TextField
                type="date"
                value={navegacao.dataAprazamento || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setNavegacao({ ...navegacao, dataAprazamento: val, turnoAprazamento: '' });
                }}
                fullWidth
                size="small"
                inputProps={{ min: moment().format('YYYY-MM-DD') }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <H3>Turno do Aprazamento <span style={{ color: 'red' }}>*</span></H3>
              <Select
                value={navegacao.turnoAprazamento || ''}
                onChange={(e) => setNavegacao({ ...navegacao, turnoAprazamento: e.target.value })}
                fullWidth
                size="small"
                displayEmpty
                disabled={!navegacao.dataAprazamento}
              >
                <MenuItem value="" disabled>Selecionar turno</MenuItem>
                {validTurnos.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <H3>Linhas de Cuidado:</H3>
            <FormGroup row style={{ marginTop: 5 }}>
              {selectedCareLines.map((cl: any) => {
                const id = cl.id || cl.linhaCuidadoId;
                const name = cl.nome || cl.linhaCuidadoNome || 'Linha';
                const checked = (navegacao.linhasCuidadoId || []).includes(id);
                return (
                  <FormControlLabel
                    key={id}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...(navegacao.linhasCuidadoId || []), id]
                            : (navegacao.linhasCuidadoId || []).filter((x: string) => x !== id);
                          setNavegacao({ ...navegacao, linhasCuidadoId: updated });
                        }}
                        color="primary"
                      />
                    }
                    label={name}
                  />
                );
              })}
            </FormGroup>
          </div>

          <div>
            <H3>Motivo <span style={{ color: 'red' }}>*</span></H3>
            <TextField
              placeholder="Digite o motivo da inclusão na navegação..."
              value={navegacao.motivo || ''}
              onChange={(e) => setNavegacao({ ...navegacao, motivo: e.target.value })}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NavegacaoField;
